import { useRef, useState } from "react";

import {
  CommentTargetType,
  WorkspaceComment,
} from "@/models/workspace/WorkspaceComment";
import { FC } from "react";
import { Button } from "@capacit-com/lara-law-shared-ui";
import { cn } from "@/lib/utils";
import { logger } from "@/lib/logger";
import ReactionHeader from "./ReactionHeader";
import useUserStore from "@/stores/user.store";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { EllipsisVerticalIcon, PencilIcon, TrashIcon } from "lucide-react";
import { ConfirmationDialog } from "@capacit-com/lara-law-shared-ui";
import CustomQuillEditor from "@/pages/workspace/components/CustomQuillEditor";
import ReactionFooter from "./ReactionFooter";
import useCommentsStore from "@/stores/comments.store";
import useWorkspaceComments from "@/hooks/useWorkspaceComments";
import { formatDate } from "./utils/DateFormat";

type CommentComponentProps = {
  comment: WorkspaceComment;
};

const CommentComponent: FC<CommentComponentProps> = ({ comment }) => {
  // Hooks
  const { me } = useUserStore();
  const {
    getCommentAuthor,
    getReactorById,
    prepareToReply,
    deletingCommentIds,
    markCommentForDeletion,
    clearDeletingComments,
    setReplyingTo,
    filter,
  } = useCommentsStore();
  const { deleteComment, updateCommentContent } = useWorkspaceComments();

  // State
  const [cardHovered, setCardHovered] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editCommentContent, setEditCommentContent] = useState(comment.content);
  const [updateLoading, setUpdateLoading] = useState<boolean>(false);
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);

  // Refs
  const cardRef = useRef<HTMLDivElement>(null);

  // Constants
  const author = getCommentAuthor(comment);

  // Handle functions
  const handleDeleteComment = async (commentId: string) => {
    try {
      // Mark comment for deletion in store
      markCommentForDeletion(comment);

      await deleteComment.mutateAsync({
        id: commentId,
      });

      // Clear deletion state after successful delete
    } catch (error) {
      logger.error("Failed to delete workspace comment", {
        operation: "deleteWorkspaceComment",
        error,
        workspaceId: comment.workspaceId,
        commentId,
      });
      clearDeletingComments();
    }
  };

  const onEditSave = async (commentId: string) => {
    try {
      setUpdateLoading(true);
      await updateCommentContent.mutateAsync({
        id: commentId,
        content: editCommentContent,
        fileId: filter !== "workspace" ? filter : undefined,
      });
      setUpdateLoading(false);
      setIsEditing(false);
    } catch (error) {
      logger.error("Failed to update workspace comment", {
        operation: "updateWorkspaceComment",
        error,
        workspaceId: comment.workspaceId,
        commentId,
      });
    }
  };

  const handleReply = () => {
    setReplyingTo(undefined);
    // Only need special handling for replies
    if (comment.targetType === CommentTargetType.Reply) {
      const commentsState = useCommentsStore.getState();

      // Find the parent comment (first-level reply)
      for (const topComment of commentsState.comments) {
        // Skip comments without replies
        if (!topComment.replies?.length) continue;

        // Option 1: Direct match - this comment is directly under a first-level reply
        const directParent = topComment.replies.find((reply) =>
          reply.replies?.some((r) => r.id === comment.id)
        );

        if (directParent) {
          prepareToReply(directParent);
          return;
        }

        // Option 2: This comment itself is a first-level reply (do nothing special)
        if (topComment.replies.some((reply) => reply.id === comment.id)) {
          break; // Found it, but no special handling needed
        }
      }
    }

    // Default: reply to this comment
    prepareToReply(comment);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditCommentContent(comment.content);
  };

  return (
    <div className="relative w-full">
      <div
        key={comment.id}
        className={cn(
          "w-full relative rounded-xl border bg-card text-card-foreground",
          deletingCommentIds.has(comment.id) &&
            "opacity-50 pointer-events-none blur-xs"
        )}
        ref={cardRef}
        onMouseEnter={() => setCardHovered(true)}
        onMouseLeave={() => setCardHovered(false)}
      >
        <div className="px-4 py-3 border-b">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">
                {author?.firstName + " " + author?.lastName || "Bruger"}
              </span>
              {Object.keys(comment.reactions || {}).length > 0 && (
                <ReactionHeader
                  reactions={comment.reactions || {}}
                  getReactor={getReactorById}
                />
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-normal text-muted-foreground">
                {formatDate(
                  comment.createdAt ? new Date(comment.createdAt) : undefined,
                  comment.updatedAt ? new Date(comment.updatedAt) : undefined
                )}
              </span>
            </div>
          </div>
        </div>

        <div className="px-4 py-3">
          {isEditing ? (
            <CustomQuillEditor
              className="w-full"
              placeholder="Redigér din kommentar..."
              value={editCommentContent}
              onChange={setEditCommentContent}
              onSubmit={() => onEditSave(comment.id)}
              disabled={updateLoading}
              buttonMessage="Gem"
              buttonLoadingMessage="Gemmer..."
              onCancel={handleCancelEdit}
              isQuillEditing={true}
            />
          ) : (
            <div
              className="comment-content"
              dangerouslySetInnerHTML={{ __html: comment.content }}
            />
          )}
        </div>
        <div className="flex flex-col items-end border-t px-4 py-3">
          <div className="relative flex items-center gap-2">
            <ReactionFooter comment={comment} />
            <div className="h-5 w-px bg-border"></div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleReply}
              className="h-5"
            >
              Svar
            </Button>
          </div>
        </div>
      </div>

      <div
        className="absolute -right-8 -top-0 -bottom-2 w-8 z-10"
        style={{
          height: cardRef.current?.offsetHeight,
        }}
        onMouseEnter={() => setCardHovered(true)}
        onMouseLeave={() => setCardHovered(false)}
      >
        {cardHovered && comment.userId === me?.id && (
          <>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="relative z-30 size-8"
                >
                  <EllipsisVerticalIcon className="size-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent side="left" align="start" className="w-28 p-1">
                <div className="flex flex-col w-full gap-1">
                  <Button
                    variant="ghost"
                    className="w-full justify-between px-2"
                    onClick={() => {
                      setOpenDeleteConfirmation(true);
                    }}
                  >
                    <span>Slet</span>
                    <TrashIcon className="size-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    className="w-full justify-between px-2"
                    onClick={() => {
                      setIsEditing(true);
                    }}
                  >
                    <span>Rediger</span>
                    <PencilIcon className="size-4" />
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          </>
        )}
      </div>
      {openDeleteConfirmation && (
        <ConfirmationDialog
          isOpen={openDeleteConfirmation}
          onClose={() => setOpenDeleteConfirmation(false)}
          title={
            comment.replies && comment.replies.length > 0
              ? "Slet kommentar og alle svar"
              : "Slet kommentar"
          }
          description={
            comment.replies && comment.replies.length > 0
              ? "Er du sikker på, at du vil slette denne kommentar og de tilhørende svar?"
              : "Er du sikker på, at du vil slette denne kommentar?"
          }
          attentionText={
            comment.replies && comment.replies.length > 0
              ? "Du skal være ekstra opmærksom på at denne handling gør også at alle svar på denne kommentar bliver slettet."
              : undefined
          }
          confirmText="Slet"
          confirmIcon={<TrashIcon className="size-4" />}
          onConfirm={async () => {
            setOpenDeleteConfirmation(false);
            await handleDeleteComment(comment.id);
          }}
          variant="destructive"
        />
      )}
      {deletingCommentIds.has(comment.id) && (
        <div className="absolute inset-0 z-30 flex items-center justify-center">
          <p className="text-lg font-semibold">Sletter...</p>
        </div>
      )}
    </div>
  );
};

export default CommentComponent;
